import React, { JSX, useContext, useEffect, useMemo, useState } from "react";
import { PciDashboardSkeleton, PciDashboardStatusEnum } from "@app/projects/network-assessment/pci-dashboard/shared";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { ClaimsInsightsDashboardContext } from "@app/projects/network-assessment/pci-dashboard/claims-insights/ClaimsInsightsDashboardContext";
import { ClaimsActivityDashboardContext } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityDashboardContext";
import PciDashboardService from "@app/projects/network-assessment/pci-dashboard/shared/pciDashboardService";
import PciDashboardDetailsView from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardDetailsView";

interface PciDashboardWrapperProps {
  children: JSX.Element[];
  showLineOfBusiness?: boolean;
}

const PciDashboardWrapper = (props: PciDashboardWrapperProps): JSX.Element => {
  const [runAgainClicked, setRunAgainClicked] = useState<boolean>(false);

  const projectContext = useContext(CountyProjectContext);
  const pciContext = props.showLineOfBusiness ? useContext(ClaimsActivityDashboardContext) : useContext(ClaimsInsightsDashboardContext);
  const pciDashboardService = new PciDashboardService();

  useEffect(() => {
    if (runAgainClicked) {
      // if there is a new pci dashboard status and the run again button was already clicked for an earlier banner,
      // reset the status
      setRunAgainClicked(false);
    }
  }, [projectContext.runConfiguration?.pciDashboardStatus]);

  const banner: JSX.Element = useMemo(() => {
    if (!!projectContext.runConfiguration?.datePublished) {
      const getRunAgainButton = (): JSX.Element => {
        return (
          <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
            <QESButton
              className="ms-3"
              disabled={runAgainClicked}
              onClick={() => onRunAgainClick()}
              qesTestId="pci-dashboard__wrapper--run-again"
              qesType="secondary"
            >
              <i className="fa fa-rocket" /> Run Again
            </QESButton>
          </RoleRestricted>
        );
      }

      switch (projectContext.runConfiguration?.pciDashboardStatus) {
        case PciDashboardStatusEnum.PublishJobQueued:
        case PciDashboardStatusEnum.PublishJobStarted:
        case PciDashboardStatusEnum.PublishTableCreationStarted:
        case PciDashboardStatusEnum.PublishTableCreationFinished:
        case PciDashboardStatusEnum.IndexCreationStarted:
        case PciDashboardStatusEnum.IndexCreationFinished:
          return (
            <QESAlert classNames="mb-3" type="info" persist={true}>
              Your PCI Dashboard data is currently being processed. Data will become available once it is complete.
            </QESAlert>
          );
        case PciDashboardStatusEnum.PublishTableCreationCanceled:
        case PciDashboardStatusEnum.IndexCreationCanceled:
          return (
            <QESAlert classNames="mb-3" type="warning" persist={true}>
              <strong>Warning!</strong> PCI Dashboard data processing has been cancelled.
              {getRunAgainButton()}
            </QESAlert>
          );
        case PciDashboardStatusEnum.PublishTableCreationFailed:
        case PciDashboardStatusEnum.IndexCreationFailed:
          return (
            <QESAlert classNames="mb-3" type="danger" persist={true}>
              <strong>Warning!</strong> An error occurred during PCI Dashboard data processing. We apologize for the inconvenience. Please contact your administrator.
              {getRunAgainButton()}
            </QESAlert>
          );
        case PciDashboardStatusEnum.PublishJobSkipped:
          return (
            <QESAlert classNames="mb-3" type="info" persist={true}>
              PCI Dashboard data for this run is not published to the legacy dashboard. We apologize for the inconvenience. Please contact your administrator.
            </QESAlert>
          );
        case PciDashboardStatusEnum.PublishJobFinished:
        default:
          break;
      }
    }
  }, [projectContext.runConfiguration?.pciDashboardStatus, runAgainClicked]);

  const getView = (): JSX.Element => {
    if (projectContext.runConfiguration?.pciDashboardStatus === PciDashboardStatusEnum.PublishJobSkipped) {
      // legacy publishing was skipped so there is no legacy data to show; the banner explains why
      return <React.Fragment />;
    }

    if (!!projectContext.runConfiguration?.datePublished && !projectContext.project?.isReadOnly && projectContext.runConfiguration?.pciDashboardStatus === PciDashboardStatusEnum.PublishJobFinished) {
      // renders appropriate pci dashboard tab
      return (
        <React.Fragment>
          <div className={pciContext.showDashboardDetails ? "d-none" : ""}>
          {props.children}
          </div>
          {(pciContext.showDashboardDetails) && (
            <PciDashboardDetailsView
              setShowDashboardDetails={pciContext.setShowDashboardDetails}
              showLineOfBusiness={props.showLineOfBusiness}
            />
          )}
        </React.Fragment>
      );
    } else {
      // renders shimmering skeleton 
      return (
        <PciDashboardSkeleton
          showLineOfBusiness={props.showLineOfBusiness}
        />
      );
    }
  }

  const onRunAgainClick = (): void => {
    setRunAgainClicked(true);
    pciDashboardService.restartPublishDashboardTable(projectContext.project.projectId, projectContext.runConfiguration.projectRunConfigurationId);
  }

  return (
    <div className="container-fluid px-0">
      <div className="row">
        <div className="col-12">
          {banner}
        </div>
      </div>
      {getView()}
    </div>
  )
}

export default PciDashboardWrapper;
